import {createSelector} from 'reselect';

import {
  getTrimmedTitleFromAnyConfig,
  normalizeValue,
  trimAndNormalizeCommaSeparatedValues,
} from '../../../components/PanelTitle/getTitleFromConfig';
import {type RootState} from '../../../types/redux';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../../util/panelbankTypes';
import {type LayedOutPanel} from '../../../util/panelTypes';
import {
  type PanelBankSectionConfigNormalized,
  type Ref as PanelBankSectionConfigRef,
} from '../panelBankSectionConfig/types';
import {makePartSelector} from '../selectors';
import type * as Types from './types';

export const makePanelSearchQuerySelector = (ref: Types.Ref) =>
  createSelector(
    makePartSelector(ref),
    panelBankConfig => panelBankConfig.settings.searchQuery
  );

export const makePanelPartsSelector = (ref: Types.Ref) => {
  return (state: RootState) => {
    const panelBankConfig = state.views.parts[ref.type][ref.id];
    return panelBankConfig.sectionRefs.flatMap(sectionRef => {
      const section = state.views.parts[sectionRef.type][sectionRef.id];
      return section.panelRefs.map(
        panelRef => state.views.parts[panelRef.type][panelRef.id]
      );
    });
  };
};

export function getRefsByPanelName(
  panels: {[id: string]: LayedOutPanel},
  panelBankSections: {[id: string]: PanelBankSectionConfigNormalized},
  sectionRefs: PanelBankSectionConfigRef[],
  displayName: string,
  sectionName?: string
) {
  // This selector reruns every time a section is expanded / collapsed,
  // and it adds a lot of overhead to that interaction. It only provides
  // a non-null result if either the sectionName or displayName are non-empty,
  // which happens only when these values are provided in the URL as query
  // parameters. So we can actually skip this selector if we are expanding
  // or collapsing a section. This if guard allows us to do that and reclaim
  // ~100s of ms of blocked JS mainthread time when expanding / collapsing
  // sections in large workspaces.
  if (sectionName === '' || displayName === '') {
    return null;
  }
  for (const sectionRef of sectionRefs) {
    const panelBankSectionConfig = panelBankSections[sectionRef.id];
    if (
      sectionName &&
      normalizeValue(sectionName) !== panelBankSectionConfig.name
    ) {
      continue;
    }
    for (const panelRef of panelBankSectionConfig.panelRefs) {
      const panelDisplayName = getTrimmedTitleFromAnyConfig(
        panels[panelRef.id].viewType,
        panels[panelRef.id]
      );
      if (
        trimAndNormalizeCommaSeparatedValues(displayName) === panelDisplayName
      ) {
        return {panelRef, panelBankSectionConfigRef: sectionRef};
      }
    }
  }
  return null;
}

export function getRefsByPanelId(
  panels: {[id: string]: LayedOutPanel},
  panelBankSections: {[id: string]: PanelBankSectionConfigNormalized},
  sectionRefs: PanelBankSectionConfigRef[],
  panelId: string
) {
  // This selector reruns every time a section is expanded / collapsed,
  // and it adds a lot of overhead to that interaction. It only provides
  // a non-null result if panelId is non-empty, which happens only when
  // panelId is provided in the URL as a query parameter. So we can actually
  // skip this selector if we are expanding or collapsing a section. This if
  // guard allows us to do that and reclaim ~100s of ms of blocked JS
  // mainthread time when expanding / collapsing sections in large workspaces.
  if (panelId === '') {
    return null;
  }
  for (const sectionRef of sectionRefs) {
    const panelBankSectionConfig = panelBankSections[sectionRef.id];
    for (const panelRef of panelBankSectionConfig.panelRefs) {
      if (panels[panelRef.id].__id__ === panelId) {
        return {panelRef, panelBankSectionConfigRef: sectionRef};
      }
    }
  }
  return null;
}

export const makePanelRefsSelector = (
  panelBankConfigRef: Types.Ref,
  opts: {panelId: string} | {displayName: string; sectionName?: string}
) => {
  return createSelector(
    (state: RootState) => state.views.parts.panel,
    (state: RootState) => state.views.parts['panel-bank-section-config'],
    (state: RootState) =>
      state.views.parts[panelBankConfigRef.type][panelBankConfigRef.id],
    (panels, panelBankSections, panelBankConfig) => {
      if ('panelId' in opts) {
        return getRefsByPanelId(
          panels,
          panelBankSections,
          panelBankConfig.sectionRefs,
          opts.panelId
        );
      }
      if ('displayName' in opts) {
        return getRefsByPanelName(
          panels,
          panelBankSections,
          panelBankConfig.sectionRefs,
          opts.displayName,
          opts.sectionName
        );
      }
      return null;
    }
  );
};

export const makeAllLinePlotSectionsAndPanelsSelector = (
  panelBankConfigRef: Types.Ref
) => {
  return createSelector(
    makePartSelector(panelBankConfigRef),
    (state: RootState) => state.views.parts['panel-bank-section-config'],
    (state: RootState) => state.views.parts.panel,
    (panelBankConfig, sectionParts, panelParts) => {
      const sections: PanelBankSectionConfigNormalized[] = [];
      const panels = [];

      const sectionRefs = panelBankConfig.sectionRefs;
      for (let idx = 0; idx < sectionRefs.length; idx += 1) {
        const section = sectionParts[sectionRefs[idx].id];
        if (
          idx === sectionRefs.length - 1 &&
          section.name === PANEL_BANK_HIDDEN_SECTION_NAME
        ) {
          continue;
        }
        sections.push(section);

        for (const panelRef of section.panelRefs) {
          const panel = panelParts[panelRef.id];

          if (panel.viewType === 'Run History Line Plot') {
            panels.push({
              config: panel.config,
              viewType: panel.viewType,
              __id__: panel.__id__,
              ref: panelRef,
            });
          }
        }
      }

      return {
        sections,
        panels,
      };
    }
  );
};

export const makeNumPanelsSelector = (ref: Types.Ref, skip: boolean) => {
  return skip
    ? () => undefined
    : (state: RootState) => {
        const panelBank = state.views.parts[ref.type][ref.id];
        return panelBank.sectionRefs.reduce((numPanels, sectionRef) => {
          const section = state.views.parts[sectionRef.type][sectionRef.id];
          return numPanels + section.panelRefs.length;
        }, 0);
      };
};

export const makeHasPanelsSelector = (ref: Types.Ref) => {
  return createSelector(
    makePartSelector(ref),
    (state: RootState) => state.views.parts['panel-bank-section-config'],
    (panelBankConfig, sectionParts) => {
      return panelBankConfig.sectionRefs.some(
        sectionRef => sectionParts[sectionRef.id].panelRefs.length > 0
      );
    }
  );
};

export const makePanelsBySectionSelector = (ref: Types.Ref) => {
  return createSelector(
    makePartSelector(ref),
    (state: RootState) => state.views.parts['panel-bank-section-config'],
    (state: RootState) => state.views.parts.panel,
    (panelBankConfig, sectionParts, panelParts) => {
      return panelBankConfig.sectionRefs.map(sectionRef => {
        const section = sectionParts[sectionRef.id];
        const panels = section.panelRefs.map(
          panelRef => panelParts[panelRef.id]
        );
        return {
          sectionRef,
          panels,
        };
      });
    }
  );
};

export const makeSectionsByNamesSelector = (
  panelBankConfigRef: Types.Ref,
  sectionNames: string[],
  skip?: boolean
) => {
  return skip
    ? () => undefined
    : createSelector(
        makePartSelector(panelBankConfigRef),
        (state: RootState) => state.views.parts['panel-bank-section-config'],
        (panelBankConfig, sectionParts) => {
          const targetSectionNames = new Set(sectionNames);
          const foundSectionRefs = new Map<string, PanelBankSectionConfigRef>();
          for (const sectionRef of panelBankConfig.sectionRefs) {
            const sectionName = sectionParts[sectionRef.id].name;
            if (
              targetSectionNames.has(sectionName) &&
              !foundSectionRefs.has(sectionName)
            ) {
              foundSectionRefs.set(sectionName, sectionRef);
              // exit early if all the sections have been found already
              if (foundSectionRefs.size === targetSectionNames.size) {
                return foundSectionRefs;
              }
            }
          }
          return foundSectionRefs;
        }
      );
};
